/**
 * Host side of the postMessage RPC bridge between the Xola seller app (parent
 * window) and embedded apps (child iframes / plugins).
 *
 * Request/response: an embedded app posts `{ type: "xola.<method>", requestId, args }`.
 * The single `message` listener routes it to a registered handler and replies with a
 * `xola.<method>.response` message carrying either a `result` or an `error`,
 * correlated by `requestId`.
 *
 * Server push: the host pushes events down to subscribed apps via `xola.event`
 * messages (route/context changes, canvas open, broadcasts).
 *
 * Module-level state (sessions, handlers, allowed origins) is shared app-wide by
 * design. There is one listener and one {@link AppSession} per embedded iframe,
 * keyed by the iframe's `Window`.
 */
import { flash } from "@xola/ui-kit";
import dayjs from "dayjs";
import { CanvasConfig, Plugin } from "types";
import { xolaUri } from "../api/xolaUri";
import { config } from "@/config";
import { useAccountStore } from "@/stores/accountStore";
import { getAccessToken, isOAuthSession, OAuthSessionStatus, useAuthStore } from "@/stores/authStore";
import { getInstalledPlugins } from "@/stores/globalStore";
import { log } from "@/utils/log";

/** Internal per-iframe state. Created by {@link registerSession}, keyed by `Window` in {@link sessions}. */
interface AppSession {
    appId: string;
    origin: string;
    isFirstParty: boolean;
    sellerId: string;
    userId: string;
    getCredential: () => Credential;
    roles: string[];
    locale: string;
    timezoneName: string;
    route: string;
    context: Record<string, unknown>;
    subscriptions: Set<string>;
    handshakeReceived: boolean;
    wakingTimer: ReturnType<typeof setTimeout> | null;
    brokenTimer: ReturnType<typeof setTimeout> | null;
    errorCount: number;
    errorWindowStart: number;
    onHandshakeFailed: ((appId: string) => void) | undefined;
    onReady: ((appId: string) => void) | undefined;
    onWaking: ((appId: string) => void) | undefined;
    onAppBroken: ((appId: string) => void) | undefined;
    onCanvasShow: ((appId: string, config: CanvasConfig) => void) | undefined;
    onCanvasHide: ((appId: string) => void) | undefined;
}

/** Caller-supplied options for {@link registerSession}. Lifecycle callbacks are optional. */
export interface SessionOptions {
    appId: string;
    origin: string;
    isFirstParty?: boolean;
    sellerId: string;
    userId: string;
    getCredential: () => Credential;
    roles: string[];
    locale: string;
    timezoneName: string;
    route: string;
    context?: Record<string, unknown>;
    onHandshakeFailed?: (appId: string) => void;
    onReady?: (appId: string) => void;
    onWaking?: (appId: string) => void;
    onAppBroken?: (appId: string) => void;
    onCanvasShow?: (appId: string, config: CanvasConfig) => void;
    onCanvasHide?: (appId: string) => void;
}

type MethodHandler = (session: AppSession, arguments_: unknown) => Promise<unknown>;

export interface Credential {
    scheme: "bearer" | "apiKey";
    token: string;
    expiresAt: number | null;
}

export const getDefaultCredential = (): Credential => {
    if (isOAuthSession()) {
        return {
            scheme: "bearer",
            token: getAccessToken() ?? "",
            expiresAt: useAuthStore.getState().expiresAt,
        };
    }

    return {
        scheme: "apiKey",
        token: useAccountStore.getState().apiKey ?? "",
        expiresAt: null,
    };
};

interface ToastArguments {
    type: "success" | "info" | "warning" | "error";
    message: string;
}

interface NavigateArguments {
    route: string;
    replace?: boolean;
}

interface AppErrorArguments {
    message?: string;
    stack?: string;
    [key: string]: unknown;
}

/**
 * Curated view of an installed plugin sent to embedded apps via `apps.listInstalled`.
 * Deliberately identity + capability only. Omits config (may hold secrets), the plugin's
 * API user, granted roles, and other apps' iframe origins/scopes.
 */
interface InstalledApp {
    id: string;
    name: string;
    summary: string;
    description: string;
    logoUrl?: string;
    version: number;
    beta: boolean;
    isEmbeddedApp: boolean;
    abilities: Record<string, unknown>;
}

const toInstalledApp = (plugin: Plugin): InstalledApp => ({
    id: plugin.id,
    name: plugin.name,
    summary: plugin.summary,
    description: plugin.description,
    logoUrl: plugin.logo?.url,
    version: plugin.version,
    beta: plugin.beta ?? false,
    isEmbeddedApp: plugin.ui?.isEmbeddedApp ?? false,
    abilities: plugin.abilities ?? {},
});

let listenerInstalled = false;
let allowedOrigins = new Set<string>();
let navigateCallback: ((route: string, replace: boolean) => void) | null = null;

const sessions = new Map<Window, AppSession>();
const canvasListeners = new Map<string, Set<() => void>>();
const handlers = new Map<string, MethodHandler>();

// App lifecycle thresholds, all measured from session registration.
const WAKING_TIMEOUT_MS = 5000; // no handshake yet, app is still "waking"
const BROKEN_TIMEOUT_MS = 60_000; // no handshake by now, handshake failed
// Circuit breaker: ERROR_THRESHOLD handler errors within ERROR_WINDOW_MS marks the app broken.
const ERROR_WINDOW_MS = 30_000;
const ERROR_THRESHOLD = 3;

/** Narrows an incoming `MessageEvent.data` to a well-formed RPC request (`xola.`-prefixed type + requestId). */
const isValidRpcMessage = (data: unknown): data is { type: string; requestId: string; args: unknown } => {
    if (typeof data !== "object" || data === null) {
        return false;
    }
    const d = data as Record<string, unknown>;
    return typeof d.type === "string" && d.type.startsWith("xola.") && typeof d.requestId === "string";
};

const emitCanvasEvent = (appId: string, event: "drawer:close" | "modal:close"): void => {
    for (const callback of canvasListeners.get(`${appId}:${event}`) ?? []) {
        callback();
    }
};

const pushEvent = (win: Window, session: AppSession, event: string, data: Record<string, unknown>): void => {
    win.postMessage({ type: "xola.event", name: event, data }, session.origin);
};

/**
 * Single `message` listener for all embedded apps. Drops anything that fails the
 * security gates (unknown origin, unregistered source window, malformed message),
 * then dispatches to the matching handler and posts the result/error back.
 * Repeated handler failures within the error window trip the circuit breaker
 * ({@link ERROR_THRESHOLD}/{@link ERROR_WINDOW_MS}) and fire `onAppBroken`.
 */
const onMessage = (event: MessageEvent): void => {
    if (!allowedOrigins.has(event.origin)) {
        return;
    }

    const source = event.source as Window | null;
    if (!source) {
        return;
    }

    const session = sessions.get(source);
    if (!session) {
        return;
    }

    if (!isValidRpcMessage(event.data)) {
        return;
    }

    const { type, requestId, args } = event.data;
    const method = type.slice("xola.".length);
    const handler = handlers.get(method);

    if (!handler) {
        source.postMessage(
            {
                type: `xola.${method}.response`,
                requestId,
                error: { code: "host_error", message: `Unknown method: ${method}` },
            },
            session.origin,
        );
        return;
    }

    const now = Date.now();
    handler(session, args)
        .then((result) => {
            source.postMessage({ type: `xola.${method}.response`, requestId, result }, session.origin);
        })
        .catch((error: Error) => {
            if (now - session.errorWindowStart > ERROR_WINDOW_MS) {
                session.errorCount = 0;
                session.errorWindowStart = now;
            }
            session.errorCount++;
            if (session.errorCount > ERROR_THRESHOLD) {
                session.onAppBroken?.(session.appId);
            }
            log("Embedded app handler error", { appId: session.appId, method, error: error.message });
            source.postMessage(
                { type: `xola.${method}.response`, requestId, error: { code: "host_error", message: error.message } },
                session.origin,
            );
        });
};

/** UTC offset in minutes for an IANA timezone; 0 on an unknown/invalid name. */
const getUtcOffsetMinutes = (timezoneName: string): number => {
    try {
        return dayjs().tz(timezoneName).utcOffset();
    } catch {
        return 0;
    }
};

const installDefaultHandlers = (): void => {
    handlers.clear();

    handlers.set("handshake", async (session) => {
        if (session.wakingTimer !== null) {
            clearTimeout(session.wakingTimer);
            session.wakingTimer = null;
        }
        if (session.brokenTimer !== null) {
            clearTimeout(session.brokenTimer);
            session.brokenTimer = null;
        }
        session.handshakeReceived = true;
        session.onReady?.(session.appId);
        const credential = session.getCredential();
        return {
            apiVersion: config.xola.apiVersion,
            appToken: credential.token,
            scheme: credential.scheme,
            token: credential.token,
            expiresAt: credential.expiresAt,
            appId: session.appId,
            apiUrl: xolaUri,
            url: session.origin,
            route: session.route,
            sellerId: session.sellerId,
            userId: session.userId,
            roles: session.roles,
            locale: session.locale,
            timezoneName: session.timezoneName,
            timezoneOffset: getUtcOffsetMinutes(session.timezoneName),
            context: session.context,
        };
    });

    handlers.set("context.get", async (session) => ({
        route: session.route,
        context: session.context,
    }));

    handlers.set("auth.getToken", async (session) => {
        const credential = session.getCredential();
        return {
            token: credential.token,
            scheme: credential.scheme,
            expiresAt: credential.expiresAt,
        };
    });

    handlers.set("ui.toast", async (_session, arguments_) => {
        const { type, message } = arguments_ as ToastArguments;
        switch (type) {
            case "success": {
                flash.success(message);

                break;
            }
            case "warning": {
                flash.warning(message);

                break;
            }
            case "info": {
                flash.info(message);

                break;
            }
            default: {
                flash.danger(message);
            }
        }
        return null;
    });

    handlers.set("ui.drawer.close", async (session) => {
        emitCanvasEvent(session.appId, "drawer:close");
        return null;
    });

    handlers.set("ui.modal.close", async (session) => {
        emitCanvasEvent(session.appId, "modal:close");
        return null;
    });

    handlers.set("navigate", async (session, arguments_) => {
        const { route, replace } = arguments_ as NavigateArguments;
        if (typeof route !== "string") {
            return null;
        }
        // First-party apps may navigate anywhere. Third-party apps are blocked until a
        // dedicated allowlist is defined for them.
        if (!session.isFirstParty) {
            return null;
        }
        navigateCallback?.(route, replace ?? false);
        return null;
    });

    handlers.set("ui.canvas.show", async (session, arguments_) => {
        session.onCanvasShow?.(session.appId, arguments_ as CanvasConfig);
        return null;
    });

    handlers.set("ui.canvas.hide", async (session) => {
        session.onCanvasHide?.(session.appId);
        return null;
    });

    handlers.set("events.subscribe", async (session, arguments_) => {
        const { events } = (arguments_ ?? {}) as { events?: string[] };
        if (Array.isArray(events)) {
            for (const e of events) {
                session.subscriptions.add(e);
            }
        }
        return null;
    });

    handlers.set("events.unsubscribe", async (session, arguments_) => {
        const { events } = (arguments_ ?? {}) as { events?: string[] };
        if (Array.isArray(events)) {
            for (const e of events) {
                session.subscriptions.delete(e);
            }
        }
        return null;
    });

    handlers.set("error", async (session, arguments_) => {
        log("Embedded app error", { appId: session.appId, ...(arguments_ as AppErrorArguments) });
        return null;
    });

    // Returns the account's installed plugins from the shared snapshot the seller app already
    // resolved (pagination + private-plugin join done in useAuthorization). No elrond call here.
    handlers.set("apps.listInstalled", async () => getInstalledPlugins().map((plugin) => toInstalledApp(plugin)));
};

installDefaultHandlers();

// ---- Public API ----

/** Installs the global message listener (once). Safe to call repeatedly. */
export const init = (): void => {
    if (!listenerInstalled) {
        window.addEventListener("message", onMessage);
        listenerInstalled = true;
    }
};

/**
 * Registers an embedded iframe so its messages are accepted, whitelists its origin,
 * and starts the waking/broken handshake timers. Call once per mounted iframe.
 */
export const registerSession = (iframeWindow: Window, opts: SessionOptions): void => {
    const session: AppSession = {
        appId: opts.appId,
        origin: opts.origin,
        isFirstParty: opts.isFirstParty ?? false,
        sellerId: opts.sellerId,
        userId: opts.userId,
        getCredential: opts.getCredential,
        roles: opts.roles,
        locale: opts.locale,
        timezoneName: opts.timezoneName,
        route: opts.route,
        context: opts.context ?? {},
        subscriptions: new Set(),
        handshakeReceived: false,
        wakingTimer: null,
        brokenTimer: null,
        errorCount: 0,
        errorWindowStart: Date.now(),
        onHandshakeFailed: opts.onHandshakeFailed,
        onReady: opts.onReady,
        onWaking: opts.onWaking,
        onAppBroken: opts.onAppBroken,
        onCanvasShow: opts.onCanvasShow,
        onCanvasHide: opts.onCanvasHide,
    };

    // Origin is added dynamically per session because plugins can have different embeddedUrl
    // origins that aren't known at init() time. registerSession is only called from our own
    // code (useEmbeddedAppSession) when we create an iframe, so this doesn't open arbitrary
    // origins. deregisterSession removes the origin when the last session using it is gone.
    allowedOrigins.add(opts.origin);

    session.wakingTimer = setTimeout(() => {
        if (!session.handshakeReceived) {
            opts.onWaking?.(opts.appId);
        }
    }, WAKING_TIMEOUT_MS);
    session.brokenTimer = setTimeout(() => {
        if (!session.handshakeReceived) {
            opts.onHandshakeFailed?.(opts.appId);
        }
    }, BROKEN_TIMEOUT_MS);

    sessions.set(iframeWindow, session);
};

/** Tears down a session on iframe unmount: clears timers, drops it, and revokes its origin if unused. */
export const deregisterSession = (iframeWindow: Window): void => {
    const session = sessions.get(iframeWindow);
    if (!session) {
        return;
    }
    if (session.wakingTimer !== null) {
        clearTimeout(session.wakingTimer);
    }
    if (session.brokenTimer !== null) {
        clearTimeout(session.brokenTimer);
    }
    sessions.delete(iframeWindow);
    const originStillInUse = [...sessions.values()].some((s) => s.origin === session.origin);
    if (!originStillInUse) {
        allowedOrigins.delete(session.origin);
    }
};

/** Wires the router callback the `navigate` handler invokes (host owns routing, not the app). */
export const setNavigateCallback = (callback: (route: string, replace: boolean) => void): void => {
    navigateCallback = callback;
};

/** True if the app opted into `event` explicitly or subscribed to all events via `"*"`. */
const isSubscribed = (session: AppSession, event: string): boolean =>
    session.subscriptions.has(event) || session.subscriptions.has("*");

/** Pushes an event to every session subscribed to it. */
export const broadcastEventToEmbeddedApps = (event: string, data: Record<string, unknown>): void => {
    for (const [win, session] of sessions) {
        if (isSubscribed(session, event)) {
            pushEvent(win, session, event, data);
        }
    }
};

/** Updates the route on all sessions and pushes `ui.navigate` to subscribers. */
export const notifyRouteChanged = (route: string): void => {
    for (const [win, session] of sessions) {
        session.route = route;
        if (isSubscribed(session, "ui.navigate")) {
            pushEvent(win, session, "ui.navigate", { route, context: session.context });
        }
    }
};

/** Sets a single session's route+context (e.g. when its canvas opens) and pushes `ui.canvas.open` if subscribed. */
export const notifyCanvasOpen = (win: Window, route: string, context: Record<string, unknown>): void => {
    const session = sessions.get(win);
    if (!session) {
        return;
    }
    session.route = route;
    session.context = context;
    if (isSubscribed(session, "ui.canvas.open")) {
        pushEvent(win, session, "ui.canvas.open", { route, context });
    }
};

/** Replaces a single session's context (route unchanged) and pushes `ui.navigate` if subscribed. */
export const updateSessionContext = (win: Window, context: Record<string, unknown>): void => {
    const session = sessions.get(win);
    if (!session) {
        return;
    }
    session.context = context;
    if (isSubscribed(session, "ui.navigate")) {
        pushEvent(win, session, "ui.navigate", { route: session.route, context });
    }
};

/** Finds the session/window entry for an app by id (one iframe per app). */
const findSessionEntryByAppId = (appId: string): [Window, AppSession] | undefined =>
    [...sessions.entries()].find(([, session]) => session.appId === appId);

/** Pushes `ui.canvas.close` to a single app when its drawer/modal closes, if subscribed. */
export const notifyCanvasClose = (appId: string, canvasType: "drawer" | "modal"): void => {
    const entry = findSessionEntryByAppId(appId);
    if (!entry) {
        return;
    }
    const [win, session] = entry;
    if (isSubscribed(session, "ui.canvas.close")) {
        pushEvent(win, session, "ui.canvas.close", { canvasType });
    }
};

/**
 * Pushes `ui.trigger` to a single app when one of its triggers is activated, if subscribed.
 * Source-agnostic on purpose: today the button/menu trigger press calls this, but any future
 * trigger kind can too; `triggerType`/`location` discriminate which one fired.
 */
export const notifyTrigger = (
    appId: string,
    payload: { location: string; triggerType: string; route: string; context: Record<string, unknown> },
): void => {
    const entry = findSessionEntryByAppId(appId);
    if (!entry) {
        return;
    }
    const [win, session] = entry;
    session.route = payload.route;
    session.context = payload.context;
    if (isSubscribed(session, "ui.trigger")) {
        pushEvent(win, session, "ui.trigger", payload);
    }
};

/**
 * Subscribes the host to an app's canvas close request (fired by the `ui.drawer.close` /
 * `ui.modal.close` handlers). Returns an unsubscribe function.
 */
export const onCanvasEvent = (
    appId: string,
    event: "drawer:close" | "modal:close",
    callback: () => void,
): (() => void) => {
    const key = `${appId}:${event}`;
    if (!canvasListeners.has(key)) {
        canvasListeners.set(key, new Set());
    }
    canvasListeners.get(key)?.add(callback);
    return () => canvasListeners.get(key)?.delete(callback);
};

// Test-only: override a handler to simulate errors or custom behavior
export const _registerHandlerForTests = (method: string, handler: MethodHandler): void => {
    handlers.set(method, handler);
};

export const _resetForTests = (): void => {
    if (listenerInstalled) {
        window.removeEventListener("message", onMessage);
        listenerInstalled = false;
    }
    allowedOrigins = new Set();
    navigateCallback = null;
    sessions.clear();
    canvasListeners.clear();
    installDefaultHandlers();

    useAuthStore.subscribe((state, previousState) => {
        if (
            previousState.status === OAuthSessionStatus.AUTHENTICATED &&
            state.status === OAuthSessionStatus.AUTHENTICATED
        ) {
            for (const [win, session] of sessions) {
                const credential = session.getCredential();
                pushEvent(win, session, "auth.grant", {
                    scheme: credential.scheme,
                    token: credential.token,
                    expiresAt: credential.expiresAt,
                });
            }
        }

        if (
            state.status === OAuthSessionStatus.SIGNED_OUT &&
            previousState.status !== OAuthSessionStatus.SIGNED_OUT &&
            isOAuthSession()
        ) {
            for (const [win, session] of sessions) {
                pushEvent(win, session, "auth.revoked", { reason: "session_ended" });
            }
        }
    });
};
